import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Search,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
} from "lucide-react";
import ReactDOM from "react-dom";
import Swal from "sweetalert2";
import api from "../../configs/api";
import defaultProfile from "../../assets-super-admin/userImg.png";
import { formatDate, getPublicPropertyRoute } from "../../utils/helpers";
import { useOutsideClick } from "../../hooks/useOutsideClick";

const OwnerDetailsModal = ({ owner, onClose }) => {
  const modalRef = React.useRef(null);
  useOutsideClick(modalRef, onClose, !!owner);

  if (!owner) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4">
      <div
        ref={modalRef}
        className="bg-white rounded-xl shadow-xl w-full max-w-3xl p-6 relative"
      >
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-800"
        >
          ✕
        </button>

        <div className="flex gap-4 items-center mb-4">
          <img
            src={owner?.profileImage || defaultProfile}
            alt="owner"
            className="w-20 h-20 rounded-full object-cover border"
          />
          <div>
            <h2 className="text-lg font-semibold text-text-primary">
              {owner?.fullName}
            </h2>
            <p className="text-sm text-text-muted">{owner?.email}</p>
            <p className="text-sm text-text-muted">{owner?.phone}</p>
            {owner?.isVerified && (
              <p className="text-sm text-status-success font-medium">
                ✔ Verified
              </p>
            )}
            <p className="text-xs text-text-muted mt-1">
              Joined: {formatDate(owner.joinedAt)}
            </p>
          </div>
        </div>

        <div className="mb-4">
          <h3 className="font-semibold text-text-primary mb-2">
            Owner Summary
          </h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-text-muted">City</p>
              <p className="text-text-primary">{owner?.city || "—"}</p>
            </div>
            <div>
              <p className="text-text-muted">Total Properties</p>
              <p className="text-text-primary">{owner.postedPropertiesCount}</p>
            </div>
            <div>
              <p className="text-text-muted">Last Property Date</p>
              <p className="text-text-primary">
                {owner.lastPropertyDate
                  ? formatDate(owner.lastPropertyDate)
                  : "—"}
              </p>
            </div>
            <div>
              <p className="text-text-muted">Subscription</p>
              <p className="text-text-primary">{owner?.subscription || "—"}</p>
            </div>
          </div>
        </div>

        <div>
          <h3 className="font-semibold text-text-primary mb-2">
            Posted Properties
          </h3>
          <div className="max-h-64 overflow-auto border border-border rounded-md p-2">
            {Array.isArray(owner.postedProperties) &&
            owner.postedProperties.length > 0 ? (
              <ul className="space-y-2">
                {owner.postedProperties.map((p) => (
                  <li
                    key={p._id}
                    className="flex items-center justify-between bg-background-card p-3 rounded-md"
                  >
                    <div>
                      <p className="font-medium text-sm text-text-primary">
                        {p.title}
                      </p>
                      <p className="text-xs text-text-muted">
                        {p.type} • {formatDate(p.createdAt)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <a
                        href={getPublicPropertyRoute(p)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm px-3 py-1.5 border rounded-md text-primary hover:bg-primary/10"
                      >
                        Open
                      </a>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-text-muted">
                No properties found for this owner.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const SuperAdminOwnerList = () => {
  const [owners, setOwners] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarViewDate, setCalendarViewDate] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);

  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [selectedOwner, setSelectedOwner] = useState(null);

  const calendarRef = useRef(null);
  const dropdownRef = useRef(null);

  useOutsideClick(calendarRef, () => setShowCalendar(false), showCalendar);
  useOutsideClick(
    dropdownRef,
    () => setDropdownOpen(null),
    dropdownOpen !== null
  );

  useEffect(() => {
    const fetchOwners = async () => {
      try {
        const res = await api.get("/owners");
        const list = res.data.list || [];
        setOwners(list);
        setFiltered(list);
      } catch (err) {
        console.error("Failed to fetch owners:", err);
      }
    };
    fetchOwners();
  }, []);

  useEffect(() => {
    const q = search.trim().toLowerCase();
    let result = owners.filter((o) => {
      const hay = `${o.user?.fullName || ""} ${o.user?.email || ""} ${
        o.user?.phone || ""
      } ${o.user?.preferredCity || ""}`.toLowerCase();
      return hay.includes(q);
    });

    if (selectedDate) {
      const sd = selectedDate.getDate();
      const sm = selectedDate.getMonth();
      const sy = selectedDate.getFullYear();
      result = result.filter((o) => {
        if (!o.lastPropertyDate) return false;
        const d = new Date(o.lastPropertyDate);
        return (
          d.getDate() === sd && d.getMonth() === sm && d.getFullYear() === sy
        );
      });
    }

    setFiltered(result);
  }, [search, selectedDate, owners]);

  const tableHeaders = [
    "No",
    "Owner Name",
    "Email",
    "Phone",
    "City",
    "Posted Properties",
    "Last Posted",
    "Joined",
    "Action",
  ];

  const MONTH_NAMES = useMemo(
    () => [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
    []
  );
  const DAY_NAMES = useMemo(
    () => ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    []
  );

  const getDaysInMonth = (date) => {
    const y = date.getFullYear();
    const m = date.getMonth();
    const first = new Date(y, m, 1);
    const last = new Date(y, m + 1, 0);
    return { daysInMonth: last.getDate(), startingDayOfWeek: first.getDay() };
  };

  const changeCalendarMonth = (inc) =>
    setCalendarViewDate(
      (p) => new Date(p.getFullYear(), p.getMonth() + inc, 1)
    );

  const handleSelectDate = (day) => {
    const chosen = new Date(
      calendarViewDate.getFullYear(),
      calendarViewDate.getMonth(),
      day
    );
    setSelectedDate(chosen);
    setShowCalendar(false);
  };

  const clearDateFilter = () => {
    setSelectedDate(null);
    setShowCalendar(false);
  };

  const CalendarDropdown = () => {
    const { daysInMonth, startingDayOfWeek } = getDaysInMonth(calendarViewDate);
    const today = new Date();

    return (
      <div className="absolute top-full mt-2 right-0 bg-white border border-border rounded-lg shadow-lg p-4 z-50 w-80">
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={() => changeCalendarMonth(-1)}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="text-sm font-semibold text-gray-800">
            {MONTH_NAMES[calendarViewDate.getMonth()]}{" "}
            {calendarViewDate.getFullYear()}
          </div>
          <button
            onClick={() => changeCalendarMonth(1)}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 mb-2">
          {DAY_NAMES.map((d) => (
            <div
              key={d}
              className="text-center text-xs font-medium text-gray-500 py-1"
            >
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {[...Array(startingDayOfWeek)].map((_, i) => (
            <div key={`e-${i}`} className="aspect-square" />
          ))}
          {[...Array(daysInMonth)].map((_, i) => {
            const day = i + 1;
            const isSelected =
              selectedDate &&
              day === selectedDate.getDate() &&
              calendarViewDate.getMonth() === selectedDate.getMonth() &&
              calendarViewDate.getFullYear() === selectedDate.getFullYear();
            const isToday =
              day === today.getDate() &&
              calendarViewDate.getMonth() === today.getMonth() &&
              calendarViewDate.getFullYear() === today.getFullYear();
            return (
              <button
                key={day}
                onClick={() => handleSelectDate(day)}
                className={`aspect-square flex items-center justify-center text-sm rounded-lg hover:bg-gray-100 ${
                  isSelected ? "bg-primary text-white" : ""
                } ${
                  isToday && !isSelected
                    ? "border border-primary text-primary"
                    : "text-gray-700"
                }`}
              >
                {day}
              </button>
            );
          })}
        </div>

        <div className="mt-3 flex justify-between">
          <button
            onClick={clearDateFilter}
            className="text-sm text-gray-600 hover:underline"
          >
            Clear
          </button>
          <button
            onClick={() => setShowCalendar(false)}
            className="text-sm text-gray-600 hover:underline"
          >
            Close
          </button>
        </div>
      </div>
    );
  };

  const handleDeleteOwner = async (ownerId) => {
    const confirm = await Swal.fire({
      title: "Delete Owner?",
      text: "This will permanently remove the owner and their listings from the admin view.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#FF4523",
      cancelButtonColor: "#735DFF",
      background: "#FFFFFF",
      color: "#000750",
      buttonsStyling: false,
      customClass: {
        popup: "rounded-xl shadow-xl border border-gray-200",
        confirmButton:
          "px-5 py-2 rounded-lg font-medium bg-status-danger text-white",
        cancelButton:
          "px-5 py-2 rounded-lg font-medium bg-primary text-white ml-2",
      },
    });

    if (!confirm.isConfirmed) return;

    try {
      await api.delete(`/owners/${ownerId}`);

      setOwners((p) =>
        p.filter((o) => o.ownerId !== ownerId && o.user?._id !== ownerId)
      );
      setFiltered((p) =>
        p.filter((o) => o.ownerId !== ownerId && o.user?._id !== ownerId)
      );

      Swal.fire({
        title: "Deleted!",
        text: "Owner removed successfully.",
        icon: "success",
        timer: 1300,
        showConfirmButton: false,
        background: "#FFFFFF",
        color: "#000750",
        iconColor: "#0CC763",
        customClass: { popup: "rounded-xl shadow-lg border border-gray-200" },
      });
      setDropdownOpen(null);
    } catch (err) {
      console.error("Delete owner error:", err);
      Swal.fire({
        title: "Error",
        text: "Failed to delete owner. Try again later.",
        icon: "error",
        confirmButtonText: "Okay",
        confirmButtonColor: "#735DFF",
      });
    }
  };

  return (
    <div className="bg-gray-50 border rounded-lg shadow-sm py-4 px-3 sm:px-6">
      <div className="mb-4">
        <h1 className="text-2xl font-semibold text-text-primary">
          Owner / Listings
        </h1>
        <p className="text-text-muted text-sm">
          Owners who have posted properties
        </p>
      </div>

      {/* Filters */}
      <div className="bg-[#F9F9F9]  rounded-md shadow-sm mb-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div className="relative max-w-md w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg"
            placeholder="Search by name, email, phone or city..."
          />
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {(search || selectedDate) && (
            <button
              onClick={() => {
                setSearch("");
                setSelectedDate(null);
              }}
              className="px-4 py-2 rounded-lg text-sm bg-red-50 text-red-600 hover:bg-red-100"
            >
              Clear All
            </button>
          )}

          <div className="relative">
            <button
              onClick={() => {
                setShowCalendar((s) => !s);
                setCalendarViewDate(selectedDate || new Date());
              }}
              className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg bg-white"
            >
              <Calendar className="w-4 h-4 text-gray-600" />
              <span className="text-sm text-[#515B73]">
                {selectedDate ? selectedDate.toLocaleDateString() : "Date"}
              </span>
              <ChevronDown className="w-4 h-4 text-gray-600" />
            </button>

            {showCalendar && (
              <div ref={calendarRef}>
                <CalendarDropdown />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-md shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead className="bg-[#E9EDF4]">
              <tr>
                {tableHeaders.map((h) => (
                  <th
                    key={h}
                    className="py-3 px-2 text-left text-gray-600 font-medium text-sm"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y">
              {filtered.map((owner, idx) => {
                const ownerId = owner.ownerId || owner.user?._id;
                return (
                  <tr key={ownerId || idx} className="hover:bg-gray-50 py-4">
                    <td className="px-2 text-sm py-4">
                      {String(idx + 1).padStart(2, "0")}
                    </td>

                    <td className="px-2 text-sm font-medium">
                      {owner?.fullName || "—"}
                    </td>
                    <td className="px-2 text-sm">{owner?.email || "—"}</td>
                    <td className="px-2 text-sm">{owner?.phone || "—"}</td>
                    <td className="px-2 text-sm">{owner?.city || "—"}</td>

                    <td className="px-2 text-sm">
                      {owner.postedPropertiesCount ??
                        (owner.postedProperties?.length || 0)}
                    </td>
                    <td className="px-2 text-sm">
                      {formatDate(owner.lastPropertyDate)}
                    </td>
                    <td className="px-2 text-sm">
                      {formatDate(owner.joinedAt)}
                    </td>

                    <td className="px-2 relative">
                      <button
                        onClick={() =>
                          setDropdownOpen(
                            dropdownOpen === ownerId ? null : ownerId
                          )
                        }
                        className="p-1"
                      >
                        <MoreVertical className="w-5 h-5 text-gray-500" />
                      </button>

                      {dropdownOpen === ownerId && (
                        <div
                          ref={dropdownRef}
                          className="absolute right-2 top-10 bg-white border shadow-lg rounded-md py-2 w-44 z-50"
                        >
                          <button
                            onClick={() => {
                              setSelectedOwner(owner);
                              setDropdownOpen(null);
                            }}
                            className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                          >
                            View Details
                          </button>
                          <button
                            onClick={() => handleDeleteOwner(ownerId)}
                            className="block w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 text-sm"
                          >
                            Delete Owner
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}

              {filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={tableHeaders.length + 1}
                    className="p-6 text-center text-sm text-gray-600"
                  >
                    No owners found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      <OwnerDetailsModal
        owner={selectedOwner}
        onClose={() => setSelectedOwner(null)}
      />
    </div>
  );
};

export default SuperAdminOwnerList;

// import { useState, useEffect, useRef } from "react";
// import {
//   Search,
//   Calendar,
//   ChevronDown,
//   MoreVertical,
//   ChevronLeft,
//   ChevronRight,
//   X,
// } from "lucide-react";
// import OwnerEditDeleteModals from "./super-admin-modals/OwnerEditDeleteModals";
// import api from "../../configs/api";

// const SuperAdminOwnerList = () => {
//   const [currentPage, setCurrentPage] = useState(1);
//   const [selectedDate, setSelectedDate] = useState(null); // Start with null - no filter
//   const [showCalendar, setShowCalendar] = useState(false);
//   const [owners, setOwners] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [openModalId, setOpenModalId] = useState(null);
//   const [showPropertyTypeDropdown, setShowPropertyTypeDropdown] =
//     useState(false);
//   const [selectedPropertyType, setSelectedPropertyType] = useState("All");
//   const [calendarViewDate, setCalendarViewDate] = useState(new Date()); // Separate state for calendar navigation

//   const calendarRef = useRef(null);
//   const ITEMS_PER_PAGE = 6;

//   const MONTH_NAMES = [
//     "January",
//     "February",
//     "March",
//     "April",
//     "May",
//     "June",
//     "July",
//     "August",
//     "September",
//     "October",
//     "November",
//     "December",
//   ];
//   const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

//   const tableHeaders = [
//     "No",
//     "Owner Name",
//     "Email ID",
//     "Type",
//     "Property Type",
//     "Listing Date",
//     "Number",
//     "Property Name",
//     "Action",
//   ];

//   const propertyTypes = ["All", "Commercial", "Land", "Residential", "Project"];

//   // Click outside to close calendar
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (calendarRef.current && !calendarRef.current.contains(event.target)) {
//         setShowCalendar(false);
//       }
//     };

//     if (showCalendar) {
//       document.addEventListener("mousedown", handleClickOutside);
//     }

//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, [showCalendar]);

//   // Fetch All Owner Leads
//   const fetchOwners = async () => {
//     setLoading(true);
//     setError(null);

//     try {
//       console.log("Fetching owners from API...");
//       const response = await api.get("/owner-lead/get-all");

//       console.log("API Response:", response.data);

//       if (response.data.success) {
//         setOwners(response.data.data);
//         console.log(`Successfully fetched ${response.data.total} owners`);
//       } else {
//         throw new Error(response.data.message || "Failed to fetch owners");
//       }
//     } catch (err) {
//       console.error("Error fetching owners:", err);
//       setError(
//         err.response?.data?.message || err.message || "Failed to fetch owners"
//       );
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Delete Owner Lead
//   const handleDelete = async (id) => {
//     try {
//       console.log(`Deleting owner with ID: ${id}`);
//       const response = await api.delete(`/owner-lead/delete/${id}`);

//       console.log("Delete Response:", response.data);

//       if (response.data.success) {
//         console.log("Owner deleted successfully");
//         fetchOwners();
//       } else {
//         throw new Error(response.data.message || "Failed to delete owner");
//       }
//     } catch (err) {
//       console.error("Error deleting owner:", err);
//       alert(
//         err.response?.data?.message || err.message || "Failed to delete owner"
//       );
//     }
//   };

//   // Fetch
//   useEffect(() => {
//     fetchOwners();
//   }, []);

//   const handleUpdateOwner = (updatedOwner) => {
//     setOwners((prev) =>
//       prev.map((o) => (o._id === updatedOwner._id ? updatedOwner : o))
//     );
//   };

//   const handlePropertyTypeSelect = (type) => {
//     setSelectedPropertyType(type);
//     setShowPropertyTypeDropdown(false);
//     setCurrentPage(1);
//   };

//   const isSameDate = (date1, date2) => {
//     if (!date1 || !date2) return false;
//     return (
//       date1.getDate() === date2.getDate() &&
//       date1.getMonth() === date2.getMonth() &&
//       date1.getFullYear() === date2.getFullYear()
//     );
//   };

//   // Filter owners based on search term, property type, AND date (if selected)
//   const filteredOwners = owners.filter((owner) => {
//     const searchLower = searchTerm.toLowerCase();
//     const matchesSearch =
//       owner.ownerName?.toLowerCase().includes(searchLower) ||
//       owner.emailId?.toLowerCase().includes(searchLower) ||
//       owner.propertyName?.toLowerCase().includes(searchLower) ||
//       owner.number?.includes(searchTerm);

//     const matchesPropertyType =
//       selectedPropertyType === "All" ||
//       owner.propertyType === selectedPropertyType;

//     // Date filtering - only apply if date is selected
//     const matchesDate = selectedDate
//       ? isSameDate(new Date(owner.createdAt), selectedDate)
//       : true;

//     return matchesSearch && matchesPropertyType && matchesDate;
//   });

//   const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
//   const endIndex = startIndex + ITEMS_PER_PAGE;
//   const paginatedOwners = filteredOwners.slice(startIndex, endIndex);
//   const totalPages = Math.ceil(filteredOwners.length / ITEMS_PER_PAGE);

//   const formatDate = (date) => {
//     if (!date) return "Select Date";
//     const day = String(date.getDate()).padStart(2, "0");
//     const month = String(date.getMonth() + 1).padStart(2, "0");
//     const year = date.getFullYear();
//     return `${day}/${month}/${year}`;
//   };

//   const getDaysInMonth = (date) => {
//     const year = date.getFullYear();
//     const month = date.getMonth();
//     const firstDay = new Date(year, month, 1);
//     const lastDay = new Date(year, month + 1, 0);
//     return {
//       daysInMonth: lastDay.getDate(),
//       startingDayOfWeek: firstDay.getDay(),
//     };
//   };

//   const changeMonth = (increment) => {
//     setCalendarViewDate(
//       new Date(
//         calendarViewDate.getFullYear(),
//         calendarViewDate.getMonth() + increment,
//         1
//       )
//     );
//   };

//   const changeYear = (increment) => {
//     setCalendarViewDate(
//       new Date(
//         calendarViewDate.getFullYear() + increment,
//         calendarViewDate.getMonth(),
//         1
//       )
//     );
//   };

//   const selectDate = (day) => {
//     const newDate = new Date(
//       calendarViewDate.getFullYear(),
//       calendarViewDate.getMonth(),
//       day
//     );
//     setSelectedDate(newDate);
//     setShowCalendar(false);
//     setCurrentPage(1);
//   };

//   const clearDateFilter = () => {
//     setSelectedDate(null);
//     setCurrentPage(1);
//   };

//   const CalendarDropdown = () => {
//     const { daysInMonth, startingDayOfWeek } = getDaysInMonth(calendarViewDate);
//     const today = new Date();

//     return (
//       <div
//         ref={calendarRef}
//         className="absolute top-full mt-2 right-0 bg-white border border-gray-200 rounded-lg shadow-xl p-5 z-50 w-[340px]"
//       >
//         {/* Year Navigation */}
//         <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-200">
//           <button
//             onClick={() => changeYear(-1)}
//             className="p-1.5 hover:bg-gray-100 rounded transition-colors"
//             title="Previous Year"
//           >
//             <ChevronLeft className="w-4 h-4 text-gray-600" />
//           </button>
//           <span className="text-base font-bold text-gray-800">
//             {calendarViewDate.getFullYear()}
//           </span>
//           <button
//             onClick={() => changeYear(1)}
//             className="p-1.5 hover:bg-gray-100 rounded transition-colors"
//             title="Next Year"
//           >
//             <ChevronRight className="w-4 h-4 text-gray-600" />
//           </button>
//         </div>

//         {/* Month Navigation */}
//         <div className="flex items-center justify-between mb-4">
//           <button
//             onClick={() => changeMonth(-1)}
//             className="p-1.5 hover:bg-gray-100 rounded transition-colors"
//             title="Previous Month"
//           >
//             <ChevronLeft className="w-5 h-5 text-gray-600" />
//           </button>
//           <span className="text-sm font-semibold text-gray-700">
//             {MONTH_NAMES[calendarViewDate.getMonth()]}
//           </span>
//           <button
//             onClick={() => changeMonth(1)}
//             className="p-1.5 hover:bg-gray-100 rounded transition-colors"
//             title="Next Month"
//           >
//             <ChevronRight className="w-5 h-5 text-gray-600" />
//           </button>
//         </div>

//         {/* Day Names */}
//         <div className="grid grid-cols-7 gap-1 mb-2">
//           {DAY_NAMES.map((day) => (
//             <div
//               key={day}
//               className="text-center text-xs font-semibold text-gray-600 py-2"
//             >
//               {day}
//             </div>
//           ))}
//         </div>

//         {/* Calendar Days */}
//         <div className="grid grid-cols-7 gap-1.5">
//           {[...Array(startingDayOfWeek)].map((_, i) => (
//             <div key={`empty-${i}`} className="aspect-square" />
//           ))}

//           {[...Array(daysInMonth)].map((_, i) => {
//             const day = i + 1;
//             const currentDate = new Date(
//               calendarViewDate.getFullYear(),
//               calendarViewDate.getMonth(),
//               day
//             );
//             const isSelected =
//               selectedDate && isSameDate(currentDate, selectedDate);
//             const isToday = isSameDate(currentDate, today);

//             return (
//               <button
//                 key={day}
//                 onClick={() => selectDate(day)}
//                 className={`aspect-square flex items-center justify-center text-sm rounded-lg transition-all hover:bg-gray-100
//                   ${
//                     isSelected
//                       ? "bg-blue-500 text-white hover:bg-blue-600 font-semibold shadow-md"
//                       : ""
//                   }
//                   ${
//                     isToday && !isSelected
//                       ? "border-2 border-blue-500 text-blue-600 font-semibold"
//                       : "text-gray-700"
//                   }
//                 `}
//               >
//                 {day}
//               </button>
//             );
//           })}
//         </div>

//         {/* Clear Button */}
//         {selectedDate && (
//           <div className="mt-4 pt-3 border-t border-gray-200">
//             <button
//               onClick={() => {
//                 clearDateFilter();
//                 setShowCalendar(false);
//               }}
//               className="w-full py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors font-medium"
//             >
//               Clear Date Filter
//             </button>
//           </div>
//         )}
//       </div>
//     );
//   };

//   const textBaseClass =
//     "text-[#333448] font-work-sans font-normal text-[13.45px] leading-[20.17px] tracking-normal sm:text-[12px] md:text-[13px] lg:text-[14px] xl:text-[15px] text-left whitespace-nowrap overflow-hidden truncate";
//   const filterButtonClass =
//     "text-[#515B73] font-montserrat font-medium text-[13.45px] leading-[100%] tracking-normal align-middle sm:text-[12px] md:text-[13px] lg:text-[14px] xl:text-[15px]";

//   return (
//     <div className="bg-gray-50 border rounded-lg shadow-sm">
//       <div>
//         <h1 className="text-[#212B36] rounded-t-lg border-b-2 bg-[#F9F9F9] px-4 py-6 font-dm-sans font-semibold text-[15.37px] leading-[14.64px] tracking-[0.5%] capitalize sm:text-[14px] md:text-[15px] lg:text-[16px] xl:text-[17px]">
//           Owner List
//         </h1>
//       </div>

//       <div className="bg-[#F9F9F9] shadow-sm p-4">
//         <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
//           {/* Search Filter */}
//           <div className="relative flex-1 max-w-md">
//             <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
//             <input
//               type="text"
//               placeholder="Search"
//               value={searchTerm}
//               onChange={(e) => setSearchTerm(e.target.value)}
//               className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
//             />
//           </div>

//           {/* Date Filter */}
//           <div className="flex items-center gap-3 flex-wrap">
//             <div className="relative">
//               <button
//                 onClick={() => setShowCalendar(!showCalendar)}
//                 className={`flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 transition-colors ${
//                   selectedDate ? "ring-2 ring-blue-200" : ""
//                 }`}
//               >
//                 <Calendar className="w-4 h-4 text-gray-600" />
//                 <span className={filterButtonClass}>
//                   {formatDate(selectedDate)}
//                 </span>
//                 {selectedDate && (
//                   <button
//                     onClick={(e) => {
//                       e.stopPropagation();
//                       clearDateFilter();
//                     }}
//                     className="ml-1 p-0.5 hover:bg-gray-200 rounded-full transition-colors"
//                   >
//                     <X className="w-3 h-3 text-gray-600" />
//                   </button>
//                 )}
//               </button>
//               {showCalendar && <CalendarDropdown />}
//             </div>

//             {/* Property Type Filter */}
//             <div className="relative">
//               <button
//                 onClick={() =>
//                   setShowPropertyTypeDropdown(!showPropertyTypeDropdown)
//                 }
//                 className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 transition-colors"
//               >
//                 <span className={filterButtonClass}>
//                   {selectedPropertyType === "All"
//                     ? "By Property Type"
//                     : selectedPropertyType}
//                 </span>
//                 <ChevronDown className="w-4 h-4 text-gray-600" />
//               </button>
//               {showPropertyTypeDropdown && (
//                 <div className="absolute top-full mt-2 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[180px]">
//                   {propertyTypes.map((type) => (
//                     <button
//                       key={type}
//                       onClick={() => handlePropertyTypeSelect(type)}
//                       className={`w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors ${
//                         selectedPropertyType === type
//                           ? "bg-blue-50 text-blue-600"
//                           : "text-gray-700"
//                       } ${type === propertyTypes[0] ? "rounded-t-lg" : ""} ${
//                         type === propertyTypes[propertyTypes.length - 1]
//                           ? "rounded-b-lg"
//                           : ""
//                       }`}
//                     >
//                       {type}
//                     </button>
//                   ))}
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>
//       </div>

//       <div className="bg-white shadow-sm overflow-hidden">
//         {loading && (
//           <div className="text-center py-8">
//             <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
//             <p className="mt-2 text-gray-600">Loading owners...</p>
//           </div>
//         )}

//         {error && (
//           <div className="text-center py-8">
//             <p className="text-red-500">{error}</p>
//             <button
//               onClick={fetchOwners}
//               className="mt-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
//             >
//               Retry
//             </button>
//           </div>
//         )}

//         {!loading && !error && (
//           <div className="overflow-x-auto">
//             <table className="w-full">
//               <thead className="bg-[#E9EDF4]">
//                 <tr>
//                   <th className="text-left px-4 whitespace-nowrap overflow-hidden truncate">
//                     <input
//                       type="checkbox"
//                       className="rounded border-gray-300"
//                     />
//                   </th>
//                   {tableHeaders.map((header) => (
//                     <th
//                       key={header}
//                       className="py-3 text-[rgba(51,52,72,0.5)] font-work-sans font-medium text-[13.45px] leading-[20.17px] tracking-normal sm:text-[12px] md:text-[13px] lg:text-[14px] xl:text-[15px] text-left whitespace-nowrap overflow-hidden truncate"
//                     >
//                       {header}
//                     </th>
//                   ))}
//                 </tr>
//               </thead>
//               <tbody className="divide-y divide-gray-200">
//                 {paginatedOwners.length === 0 ? (
//                   <tr>
//                     <td
//                       colSpan={tableHeaders.length + 1}
//                       className="text-center py-8 text-gray-500"
//                     >
//                       No owners found
//                     </td>
//                   </tr>
//                 ) : (
//                   paginatedOwners.map((owner, index) => (
//                     <tr
//                       key={owner._id}
//                       className="hover:bg-gray-50 transition-colors"
//                     >
//                       <td className="px-4 py-4 text-left whitespace-nowrap overflow-hidden truncate">
//                         <input
//                           type="checkbox"
//                           className="rounded border-gray-300"
//                         />
//                       </td>
//                       <td className={textBaseClass}>
//                         {owner.no ||
//                           String(startIndex + index + 1).padStart(2, "0")}
//                       </td>
//                       <td className={textBaseClass}>{owner.ownerName}</td>
//                       <td className={textBaseClass}>{owner.emailId}</td>
//                       <td className={textBaseClass}>{owner.userType}</td>
//                       <td className={textBaseClass}>{owner.propertyType}</td>
//                       <td className={textBaseClass}>
//                         {(() => {
//                           const date = new Date(owner.createdAt);
//                           const day = String(date.getDate()).padStart(2, "0");
//                           const month = String(date.getMonth() + 1).padStart(
//                             2,
//                             "0"
//                           );
//                           const year = date.getFullYear();
//                           return `${day}-${month}-${year}`;
//                         })()}
//                       </td>
//                       <td className={textBaseClass}>{owner.number}</td>
//                       <td className="px-4 py-4 text-left whitespace-nowrap overflow-hidden truncate">
//                         {owner.propertyName}
//                       </td>
//                       <td className="px-4 py-4 text-left whitespace-nowrap overflow-hidden truncate">
//                         <button
//                           onClick={() =>
//                             setOpenModalId(
//                               openModalId === owner._id ? null : owner._id
//                             )
//                           }
//                           className="text-gray-500 hover:text-gray-700"
//                         >
//                           <MoreVertical size={18} />
//                         </button>
//                       </td>

//                       {openModalId === owner._id && (
//                         <div className="absolute right-16 bottom-40 z-50">
//                           <OwnerEditDeleteModals
//                             owner={owner}
//                             onClose={() => setOpenModalId(null)}
//                             onDelete={() => handleDelete(owner._id)}
//                             onUpdate={(updatedOwner) =>
//                               handleUpdateOwner(updatedOwner)
//                             }
//                           />
//                         </div>
//                       )}
//                     </tr>
//                   ))
//                 )}
//               </tbody>
//             </table>
//           </div>
//         )}
//       </div>

//       {/* Pagination */}
//       <div className="flex flex-col sm:flex-row items-center justify-between mt-10 px-6 py-4 border-t border-gray-200 gap-4">
//         <div className="text-sm text-gray-600">
//           Showing {paginatedOwners.length > 0 ? startIndex + 1 : 0}-
//           {startIndex + paginatedOwners.length} of {filteredOwners.length}
//         </div>
//         <div className="flex items-center gap-2">
//           <button
//             onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
//             disabled={currentPage === 1}
//             className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
//           >
//             Prev
//           </button>
//           {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
//             let pageNum;
//             if (totalPages <= 5) {
//               pageNum = i + 1;
//             } else if (currentPage <= 3) {
//               pageNum = i + 1;
//             } else if (currentPage >= totalPages - 2) {
//               pageNum = totalPages - 4 + i;
//             } else {
//               pageNum = currentPage - 2 + i;
//             }

//             return (
//               <button
//                 key={pageNum}
//                 className={`px-3 py-1 text-sm rounded transition-colors ${
//                   currentPage === pageNum
//                     ? "bg-blue-500 text-white"
//                     : "text-gray-600 hover:bg-gray-100"
//                 }`}
//                 onClick={() => setCurrentPage(pageNum)}
//               >
//                 {pageNum}
//               </button>
//             );
//           })}
//           <button
//             onClick={() =>
//               setCurrentPage(Math.min(totalPages, currentPage + 1))
//             }
//             disabled={currentPage === totalPages}
//             className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
//           >
//             Next
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default SuperAdminOwnerList;
